import React from 'react';
import { Camera, Languages, Award, MessagesSquare, LogIn, HelpCircle, CheckCircle2, LayoutGrid, MessageSquarePlus } from 'lucide-react';

const IMG = process.env.PUBLIC_URL + '/manual';

function Step({ n, children }) {
  return (
    <li className="flex items-start gap-3">
      <span className="flex-shrink-0 w-8 h-8 rounded-full bg-cmic-600 text-white text-base font-bold flex items-center justify-center">{n}</span>
      <span className="text-lg leading-relaxed text-charcoal-700 pt-0.5">{children}</span>
    </li>
  );
}

function Figure({ src, alt, caption, size }) {
  const sizeClass = size === 'small' ? 'max-w-[260px]' : size === 'medium' ? 'max-w-md' : 'w-full';
  return (
    <figure className="mt-2 flex flex-col items-center">
      <img src={`${IMG}/${src}`} alt={alt} loading="lazy"
        className={`${sizeClass} w-full rounded-xl border border-gray-200 shadow-sm`} />
      <figcaption className="mt-2 text-center text-sm text-charcoal-400 italic">{caption || 'Así se ve en tu pantalla'}</figcaption>
    </figure>
  );
}

function Section({ icon: Icon, title, intro, images = [], children, tip }) {
  return (
    <section data-testid={`manual-section-${title}`} className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
      <div className="flex items-center gap-3 px-6 py-5 bg-cmic-50 border-b border-cmic-100">
        <span className="flex-shrink-0 w-11 h-11 rounded-xl bg-cmic-600 text-white flex items-center justify-center">
          <Icon size={22} />
        </span>
        <h3 className="text-xl sm:text-2xl font-bold text-charcoal-800">{title}</h3>
      </div>
      <div className="p-6 space-y-5">
        {intro && <p className="text-lg leading-relaxed text-charcoal-600">{intro}</p>}
        <ol className="space-y-4">{children}</ol>
        {images.map((im) => <Figure key={im.src} {...im} />)}
        {tip && (
          <div className="flex items-start gap-3 bg-emerald-50 border border-emerald-200 rounded-xl p-4">
            <CheckCircle2 size={22} className="text-emerald-600 flex-shrink-0 mt-0.5" />
            <p className="text-base text-emerald-800 leading-relaxed">{tip}</p>
          </div>
        )}
      </div>
    </section>
  );
}

export default function UserManual({ forumEnabled = true }) {
  return (
    <div data-testid="user-manual" className="space-y-8 max-w-3xl">
      <div className="bg-gradient-to-br from-cmic-600 to-cmic-800 text-white rounded-2xl p-7 shadow-sm">
        <div className="flex items-center gap-3 mb-2">
          <HelpCircle size={26} />
          <h2 className="text-2xl sm:text-3xl font-bold">Manual de usuario</h2>
        </div>
        <p className="text-lg text-cmic-50 leading-relaxed">
          Bienvenido/a. Esta guía te explica, paso a paso y con imágenes, cómo usar tu perfil.
          Tómate tu tiempo: no hay prisa y no puedes descomponer nada. Si algo no te queda claro,
          al final te decimos cómo pedir ayuda.
        </p>
      </div>

      <Section
        icon={LogIn}
        title="1. Cómo entrar a tu cuenta"
        intro="Para usar tu perfil primero debes “iniciar sesión” (entrar con tu usuario y contraseña). Hay dos lugares donde encontrar el botón de acceso:"
        images={[
          { src: 'menu_cmic.png', alt: 'Menú CMIC desplegado con la opción Acceso miembros', caption: 'Opción 1: menú “CMIC” → “Acceso miembros”', size: 'small' },
          { src: 'footer_acceso.png', alt: 'Pie de página con el botón Acceso Miembro', caption: 'Opción 2: hasta abajo de la página, botón “Acceso Miembro”' },
          { src: 'login.png', alt: 'Pantalla de acceso al portal con usuario y contraseña', caption: 'Escribe aquí tu usuario y contraseña' },
        ]}
        tip="Guarda tu usuario y contraseña en un lugar seguro. Recuerda: el usuario NO siempre es tu correo. Si lo olvidaste, presiona “¿Olvidaste tu contraseña?”."
      >
        <Step n="1">Arriba, en el menú <strong>“CMIC”</strong>, presiona y elige <strong>“Acceso miembros”</strong>.</Step>
        <Step n="2">O baja hasta el final de la página y presiona el botón <strong>“Acceso Miembro”</strong>.</Step>
        <Step n="3">Escribe tu <strong>usuario</strong> y tu <strong>contraseña</strong>.</Step>
        <Step n="4">Presiona el botón azul <strong>“Iniciar Sesión”</strong>. ¡Listo, ya estás dentro!</Step>
        <Step n="5">Para <strong>salir</strong>, presiona tu nombre arriba a la derecha y elige <strong>“Cerrar sesión”</strong>.</Step>
      </Section>

      <Section
        icon={Camera}
        title="2. Cambiar tu foto de perfil"
        intro="Tu foto ayuda a que los clientes te reconozcan. Cambiarla es muy fácil:"
        images={[{ src: 'foto.png', alt: 'Sección de foto de perfil con el botón Subir imagen' }]}
        tip="Consejo: usa una foto tuya de frente, con buena luz y fondo sencillo. Tu foto se guarda al instante."
      >
        <Step n="1">Arriba, entra a la pestaña <strong>“Editar perfil”</strong>.</Step>
        <Step n="2">Busca el recuadro que dice <strong>“Foto de perfil”</strong> (arriba a la izquierda).</Step>
        <Step n="3">Presiona el botón <strong>“Subir imagen”</strong>.</Step>
        <Step n="4">Se abrirá una ventana. Busca la foto en tu computadora o teléfono y selecciónala.</Step>
        <Step n="5">Podrás <strong>ajustar y recortar</strong> la foto. Cuando te guste, presiona <strong>“Guardar”</strong>.</Step>
      </Section>

      <Section
        icon={Languages}
        title="3. Poner tus idiomas"
        intro="Los idiomas son lo más importante de tu perfil. Se dividen en dos grupos:"
        images={[{ src: 'idiomas.png', alt: 'Sección de idiomas activos y pasivos con etiquetas' }]}
        tip="Importante: los cambios de idiomas los revisa primero el Consejo Directivo antes de aparecer en el directorio. Es normal que tarden un poco en publicarse."
      >
        <Step n="1">En <strong>“Editar perfil”</strong>, baja hasta la sección <strong>“Idiomas”</strong>.</Step>
        <Step n="2"><strong>Idiomas activos</strong>: son los idiomas HACIA los que interpretas (por ejemplo, hacia el Español).</Step>
        <Step n="3"><strong>Idiomas pasivos</strong>: son los idiomas DESDE los que entiendes, pero hacia los que no interpretas.</Step>
        <Step n="4">Para agregar uno, escribe el idioma en la casilla y presiona la tecla <strong>“Enter”</strong> de tu teclado.</Step>
        <Step n="5">Para quitar un idioma, presiona la <strong>“✕”</strong> que aparece junto a su nombre.</Step>
        <Step n="6">Las combinaciones (por ejemplo “Español → Inglés”) <strong>se crean solas</strong>. Tú no las escribes.</Step>
      </Section>

      <Section
        icon={Award}
        title="4. Agregar tus certificaciones"
        intro="Aquí escribes tus estudios, diplomas o membresías. Se mostrarán numeradas en tu perfil."
        images={[{ src: 'certificaciones.png', alt: 'Lista de certificaciones numeradas 1, 2, 3' }]}
        tip="Escribe una sola certificación en cada casilla. Se numeran automáticamente."
      >
        <Step n="1">En <strong>“Editar perfil”</strong>, baja hasta la sección <strong>“Certificaciones”</strong>.</Step>
        <Step n="2">Escribe una certificación en la casilla número <strong>1</strong>.</Step>
        <Step n="3">Para agregar otra, presiona <strong>“+ Agregar certificación”</strong>. Aparecerá una nueva casilla.</Step>
        <Step n="4">Si te equivocaste o quieres borrar una, presiona la <strong>“✕”</strong> a su derecha.</Step>
        <Step n="5">Al terminar, baja y presiona <strong>“Guardar cambios”</strong>.</Step>
      </Section>

      <Section
        icon={LayoutGrid}
        title="5. El área interna (Hub)"
        intro={`El área interna es un espacio privado, solo para miembros del Colegio. Se llega desde el botón “Interno”. Tiene ${forumEnabled ? 'cuatro' : 'tres'} pestañas:`}
        images={[{ src: 'foro.png', alt: 'Hub Interno con pestañas Noticias, Eventos y Avisos' }]}
        tip="Todo lo que hay aquí es privado: solo lo ven los miembros del Colegio, nunca el público."
      >
        <Step n="1"><strong>Noticias internas</strong>: novedades y comunicados del Colegio.</Step>
        <Step n="2"><strong>Eventos internos</strong>: cursos, reuniones y actividades.</Step>
        <Step n="3"><strong>Avisos</strong>: el tablón oficial con avisos importantes.</Step>
        {forumEnabled && <Step n="4"><strong>Foro</strong>: el espacio para conversar con tus colegas (te lo explicamos abajo con detalle).</Step>}
      </Section>

      {forumEnabled && (
      <Section
        icon={MessagesSquare}
        title="6. El Foro: conversa con tus colegas"
        intro="El Foro es como un tablón de conversaciones. Está organizado por temas (llamados “categorías”). Dentro de cada categoría hay “hilos”, que son las conversaciones."
        images={[{ src: 'foro_categorias.png', alt: 'Lista de categorías del foro: Avisos, General, Social, Trabajos', caption: 'Paso A: elige una categoría (tema)' }]}
        tip="A la derecha verás “Actividad reciente”: son las últimas conversaciones. Puedes presionar cualquiera para abrirla directamente."
      >
        <Step n="1">Entra al área interna y presiona la pestaña <strong>“Foro”</strong>.</Step>
        <Step n="2">Verás una lista de <strong>categorías</strong> (por ejemplo: Avisos, General, Social, Trabajos). Cada una es un tema distinto.</Step>
        <Step n="3">A la derecha de cada categoría dice cuántos <strong>“hilos”</strong> (conversaciones) tiene.</Step>
        <Step n="4">Presiona la categoría que te interese para ver sus conversaciones.</Step>
      </Section>
      )}

      {forumEnabled && (
      <Section
        icon={MessageSquarePlus}
        title="7. Leer una conversación y responder"
        intro="Cuando abres un hilo (una conversación), puedes leer todo lo que se ha escrito y, si quieres, dejar tu respuesta."
        images={[{ src: 'foro_hilo.png', alt: 'Vista de una conversación del foro con mensaje y caja para responder', caption: 'Paso B: lee y, si gustas, escribe tu respuesta abajo' }]}
        tip="Sé siempre respetuoso/a. Todos somos colegas. Si un mensaje no te parece adecuado, avísale al equipo desde la pestaña “Soporte”."
      >
        <Step n="1">Presiona el título de la conversación que quieras <strong>leer</strong>.</Step>
        <Step n="2">Verás quién escribió, cuándo, y el mensaje completo.</Step>
        <Step n="3">Para participar, baja hasta la caja que dice <strong>“Escribe tu respuesta…”</strong>.</Step>
        <Step n="4">Escribe tu mensaje. Puedes ponerlo en <strong>negritas</strong> o hacer listas con los botones de arriba de la caja.</Step>
        <Step n="5">Cuando termines, presiona <strong>“Responder”</strong> para publicarlo.</Step>
        <Step n="6">Para volver atrás, usa la flecha <strong>“← [nombre de la categoría]”</strong> de arriba.</Step>
      </Section>
      )}

      <div className="bg-cmic-50 border border-cmic-200 rounded-2xl p-6 text-center">
        <HelpCircle size={30} className="text-cmic-600 mx-auto mb-2" />
        <h3 className="text-xl font-bold text-charcoal-800 mb-1">¿Necesitas ayuda?</h3>
        <p className="text-lg text-charcoal-600 leading-relaxed">
          No te preocupes si algo no te sale a la primera. Entra a la pestaña <strong>“Soporte”</strong> (aquí mismo
          en tu perfil) y escríbenos con toda confianza. Con gusto te ayudamos paso a paso.
        </p>
      </div>
    </div>
  );
}
